import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle, ExternalLink, FileText, Gavel, Receipt, RefreshCw, Undo2,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { hardwareApi, type AgreementAuditRow, type DeviceUnitRow } from '../../services/hardwareApi';
import { RefundModal } from '../RefundModal';
import { Card, CardHeader, Chip, Empty, Note } from './financeUi';
import { money, shortDate, shortDateTime } from './financeFormat';
import type { PaymentEvent } from '../../types';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → HISTORY
 *
 * Spec §9: invoices, payments, audit of every override.
 *
 * Three sources, kept apart on purpose rather than merged into one stream:
 *   - Invoices, straight from Stripe.
 *   - Payments, from the webhook events this platform recorded (refundable here).
 *   - The agreement audit trail — every waive, settlement and rollover, with who,
 *     when, how much and why. None of them is reversible.
 */

interface Props {
  tenantId: string;
  /** Recorded Stripe webhook events, already loaded by the tenant page. */
  billingEvents: PaymentEvent[];
  /** Units with a finance agreement — their audit trails are read here. */
  units: DeviceUnitRow[];
  /**
   * Why `units` is empty, when it is empty because the read FAILED.
   *
   * Without it this panel cannot tell "no device has ever been waived" apart
   * from "hardware-service is down", and it would print the first while the
   * second is true — the exact claim it refuses to make for invoices twelve
   * lines above.
   */
  hardwareError: string | null;
  hardwareLoading: boolean;
  modelNames: Record<string, string>;
  onReload: () => Promise<void> | void;
  notify: (message: string) => void;
}

const PAYMENT_EVENT_TYPES = [
  'invoice.paid', 'refund', 'checkout.session.completed',
  'checkout.session.completed.existing_tenant', 'invoice.payment_failed',
];

const EVENT_LABEL: Record<string, string> = {
  'invoice.paid': 'Payment successful',
  'invoice.payment_failed': 'Payment failed',
  refund: 'Refund',
  'checkout.session.completed': 'New subscription',
  'checkout.session.completed.existing_tenant': 'Subscription renewed',
};

/** How many units' audit trails to read. Each is one request; a tenant with a
 *  hundred devices should not fire a hundred of them to render a panel. */
const AUDIT_UNIT_CAP = 25;

const INVOICE_TONE: Record<string, string> = {
  paid: 'emerald', open: 'amber', draft: 'stone', uncollectible: 'red', void: 'stone',
};

const HistoryPanel: React.FC<Props> = ({
  tenantId, billingEvents, units, hardwareError, hardwareLoading, modelNames, onReload, notify,
}) => {
  const [invoices, setInvoices] = useState<any[] | null>(null);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [audit, setAudit] = useState<AgreementAuditRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTruncated, setAuditTruncated] = useState(false);
  const [auditUnread, setAuditUnread] = useState(0);

  const [refundOpen, setRefundOpen] = useState(false);
  const [refundCharge, setRefundCharge] = useState<{ id: string; amount: number; currency: string } | null>(null);

  const payments = useMemo(
    () => billingEvents.filter((ev) => PAYMENT_EVENT_TYPES.includes(ev.type)),
    [billingEvents],
  );

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const res = await api.superAdmin.getTenantInvoices(tenantId, 25);
      setInvoices(res?.invoices || []);
    } catch (err: any) {
      // An unreadable invoice list is NOT an empty invoice list — say which.
      setInvoices(null);
      setInvoicesError(err?.message || 'Stripe invoices could not be read.');
    } finally {
      setInvoicesLoading(false);
    }
  };
  useEffect(() => { loadInvoices(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenantId]);

  const financedUnitIds = useMemo(
    () => units.filter((u) => !!u.agreement).map((u) => u.unitId),
    [units],
  );

  const loadAudit = async () => {
    if (financedUnitIds.length === 0) { setAudit([]); setAuditTruncated(false); setAuditUnread(0); return; }
    setAuditLoading(true);
    const ids = financedUnitIds.slice(0, AUDIT_UNIT_CAP);
    setAuditTruncated(financedUnitIds.length > ids.length);
    const results = await Promise.allSettled(ids.map((id) => hardwareApi.getUnit(id)));
    const rows: AgreementAuditRow[] = [];
    // A device whose trail could not be read is COUNTED, not dropped. Silently
    // discarding it would shorten this list without saying so, and a short
    // audit trail that looks complete is worse than none at all.
    let unread = 0;
    for (const r of results) {
      if (r.status === 'fulfilled') rows.push(...(r.value.audit || []));
      else unread += 1;
    }
    rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    setAudit(rows);
    setAuditUnread(unread);
    setAuditLoading(false);
  };
  useEffect(() => { loadAudit(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financedUnitIds.join(',')]);

  const handleRefund = async (chargeId: string, amount?: number, reason?: string) => {
    try {
      await api.superAdmin.refundPayment(chargeId, amount, reason);
      notify('Refund processed successfully');
      await onReload();
    } catch (err: any) {
      notify(`Refund failed: ${err?.message || 'unknown error'}`);
    }
  };

  const unitLabel = (unitId: string) => {
    const u = units.find((x) => x.unitId === unitId);
    if (!u) return unitId;
    return `${modelNames[u.modelId] || u.modelId}${u.serialNumber ? ` ${u.serialNumber}` : ''}`;
  };

  return (
    <div className="space-y-4 text-stone-800">
      {/* Invoices ------------------------------------------------------------ */}
      <Card>
        <CardHeader
          icon={<FileText size={15} />}
          title="Invoices"
          hint="from Stripe"
          actions={
            <button
              onClick={loadInvoices}
              disabled={invoicesLoading}
              className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5 min-h-[36px] px-2"
            >
              {invoicesLoading ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />} refresh
            </button>
          }
        />
        {invoicesLoading && invoices === null && !invoicesError ? (
          <RubyLoader variant="section" className="py-10" />
        ) : invoicesError ? (
          <div className="p-4">
            <Note tone="warn">
              <strong>Invoices could not be read.</strong> {invoicesError} That is not the same as this tenant having
              no invoices — nothing was returned.
            </Note>
          </div>
        ) : (invoices || []).length === 0 ? (
          <Empty icon={<FileText size={34} />} title="No invoices on this Stripe customer yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-stone-800">
              <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-500 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">Invoice</th>
                  <th className="px-3 py-2 font-semibold">Period</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold text-right">Amount</th>
                  <th className="px-3 py-2 font-semibold text-right">Paid</th>
                  <th className="px-3 py-2 font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {(invoices || []).map((inv) => (
                  <tr key={inv.id} className="hover:bg-stone-50">
                    <td className="px-3 py-2.5 font-mono text-xs text-stone-600">{inv.number || inv.id}</td>
                    <td className="px-3 py-2.5 text-[11px] text-stone-600">
                      {shortDate(inv.periodStart)} → {shortDate(inv.periodEnd)}
                    </td>
                    <td className="px-3 py-2.5">
                      <Chip tone={INVOICE_TONE[inv.status] || 'stone'}>{inv.status}</Chip>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{money(inv.amount, inv.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-stone-600">
                      {money(inv.amountPaid, inv.currency)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {inv.hostedInvoiceUrl && (
                        <a
                          href={inv.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 text-xs"
                        >
                          View <ExternalLink size={11} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payments ------------------------------------------------------------- */}
      <Card>
        <CardHeader icon={<Receipt size={15} />} title="Payments" hint={`${payments.length} events`} />
        {payments.length === 0 ? (
          <Empty icon={<Receipt size={34} />} title="No payment events recorded yet" />
        ) : (
          <div className="divide-y divide-stone-100 max-h-[480px] overflow-y-auto">
            {payments.map((ev, i) => {
              const amount = ev.amountPaid ?? ev.amount ?? ev.amountDue ?? null;
              const failure = ev.type === 'invoice.payment_failed';
              const refund = ev.type === 'refund';
              const refundable = ['invoice.paid', 'checkout.session.completed'].includes(ev.type) && !!ev.chargeId;
              return (
                <div key={`${ev.chargeId || ev.invoiceId || 'ev'}-${i}`} className="px-4 py-3 hover:bg-stone-50 group">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                        refund ? 'bg-rose-100' : failure ? 'bg-red-100' : 'bg-emerald-100'
                      }`}>
                        {refund ? <Undo2 size={16} className="text-rose-600" />
                          : failure ? <AlertTriangle size={16} className="text-red-600" />
                            : <CheckCircle size={16} className="text-emerald-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className={`font-medium text-sm ${
                          refund ? 'text-rose-700' : failure ? 'text-red-700' : 'text-stone-800'
                        }`}>
                          {EVENT_LABEL[ev.type] || ev.type.replace(/\./g, ' ')}
                        </p>
                        <p className="text-stone-400 text-xs">{shortDateTime(ev.eventTimestamp)}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-base font-bold tabular-nums ${
                        refund ? 'text-rose-600' : failure ? 'text-red-600' : 'text-stone-800'
                      }`}>
                        {amount !== null ? money(amount, ev.currency) : '—'}
                      </p>
                      {refundable && amount && amount > 0 && (
                        <button
                          onClick={() => {
                            setRefundCharge({ id: ev.chargeId!, amount, currency: ev.currency || 'usd' });
                            setRefundOpen(true);
                          }}
                          className="text-xs text-stone-600 hover:text-stone-900 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                        >
                          Refund
                        </button>
                      )}
                    </div>
                  </div>
                  {(ev.invoiceId || ev.chargeId) && (
                    <div className="mt-1.5 ml-12 flex gap-4 text-xs">
                      {ev.invoiceId && (
                        <a href={`https://dashboard.stripe.com/invoices/${ev.invoiceId}`} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-stone-800 inline-flex items-center gap-1">
                          Invoice <ExternalLink size={11} />
                        </a>
                      )}
                      {ev.chargeId && (
                        <a href={`https://dashboard.stripe.com/payments/${ev.chargeId}`} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-stone-800 inline-flex items-center gap-1">
                          Payment <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Overrides ------------------------------------------------------------- */}
      <Card>
        <CardHeader
          icon={<Gavel size={15} />}
          title="Agreement overrides"
          hint="who · when · how much · why"
          actions={
            <button
              onClick={loadAudit}
              disabled={auditLoading}
              className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5 min-h-[36px] px-2"
            >
              {auditLoading ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />} refresh
            </button>
          }
        />
        {(auditLoading || hardwareLoading) && audit.length === 0 && !hardwareError ? (
          <RubyLoader variant="section" className="py-10" />
        ) : hardwareError ? (
          <div className="p-4">
            <Note tone="warn">
              <strong>The device list could not be read, so the audit trail could not be either.</strong>{' '}
              {hardwareError} This is <em>not</em> the same as no agreement having been waived or settled — nothing
              was returned to look through.
            </Note>
          </div>
        ) : audit.length === 0 && auditUnread > 0 ? (
          <div className="p-4">
            <Note tone="warn">
              <strong>{auditUnread} device{auditUnread === 1 ? '' : 's'} could not be read</strong>, and no audit row
              came back from the rest. Whether any agreement has been waived or settled is unknown until those reads
              succeed — try refresh.
            </Note>
          </div>
        ) : audit.length === 0 ? (
          <Empty
            icon={<Gavel size={34} />}
            title="No agreement has been waived, settled or rolled over"
            text="Every one of those actions writes a row here in the same transaction as the change itself, so this being empty means none has happened."
          />
        ) : (
          <div className="divide-y divide-stone-100">
            {audit.map((row) => (
              <div key={`${row.unitId}-${row.createdAt}`} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Chip tone={row.action === 'waive' ? 'amber' : row.action === 'settle' ? 'blue' : 'violet'}>
                      {row.action}
                    </Chip>
                    <span className="text-sm text-stone-800 truncate">{unitLabel(row.unitId)}</span>
                  </div>
                  <span className="text-sm font-bold tabular-nums text-stone-900">{money(row.amountCents)}</span>
                </div>
                <p className="text-xs text-stone-600 mt-1">{row.reason}</p>
                <p className="text-[11px] text-stone-400 mt-0.5">
                  {row.actorEmail || row.actor || 'unknown actor'} · {shortDateTime(row.createdAt)} ·{' '}
                  {row.agreementStatusBefore} → {row.agreementStatusAfter} ·{' '}
                  {money(row.paidCents)} of {money(row.principalCents)} paid at the time
                </p>
              </div>
            ))}
          </div>
        )}
        <div className="px-4 py-3 border-t border-stone-200 space-y-2">
          {auditUnread > 0 && audit.length > 0 && (
            <Note tone="warn">
              {auditUnread} device{auditUnread === 1 ? "'s trail" : "s' trails"} could not be read, so this list is
              incomplete — there may be overrides it is not showing.
            </Note>
          )}
          {auditTruncated && (
            <Note tone="warn">
              Only the first {AUDIT_UNIT_CAP} financed devices were read. Older overrides on the rest are on each
              device, not lost.
            </Note>
          )}
          <p className="text-[11px] text-stone-500 leading-relaxed">
            Plan changes, impersonation and password resets are recorded separately and stay on the{' '}
            <strong className="text-stone-600">Admin</strong> tab's audit log.
          </p>
        </div>
      </Card>

      <RefundModal
        open={refundOpen}
        onClose={() => { setRefundOpen(false); setRefundCharge(null); }}
        chargeId={refundCharge?.id || ''}
        maxAmount={refundCharge?.amount || 0}
        currency={refundCharge?.currency || 'usd'}
        onRefund={handleRefund}
      />
    </div>
  );
};

export default HistoryPanel;
